/**
 * `RuleEditor` (web.md §4; the prototype's `ruleEditor()` + `testRule()`): shared by Filters and the
 * scoring rules of Models. Sections Rule (name, icon — fix 4), When to run (filters only), Match
 * conditions (groups with ALL/ANY inside, one AND/OR across), Then (actions), and the Test rule / Run
 * now row. The Enabled switch is in the editor header (EditorFrame), never here (fix 7).
 *
 * The caller owns the draft (useDraft) and the frame; this component renders the form body.
 */

import { useState, type ReactNode } from 'react';
import type { ConditionField, ConditionNode, Model, ModelId, QueryUse, RuleId, RuleTestResult } from '@digester/core';
import { useEngine } from '../../engine-client/EngineProvider.js';
import { useFolders } from '../../app/hooks.js';
import { reportError } from '../../app/errors.js';
import { showToast } from '../../app/Toast.js';
import { IconField } from '../../icons/IconField.js';
import { Icon } from '../../icons/Icon.js';
import { Field, Section, Select } from '../../ui/form.js';
import { Tool } from '../../ui/Tool.js';
import { SOURCE_KIND_LABELS, sourceKindIcon } from '../../ui/source-icons.js';
import { SaveIslandTools } from '../shell/index.js';
import { useSources, useTags, useTopics } from './data.js';
import { useModels } from '../models/data.js';
import { MultiPicker, type PickerOption } from './MultiPicker.js';
import {
  FIELD_LABELS,
  FIELD_OPERATORS,
  OPERATOR_LABELS,
  SCRIPT_LABELS,
  SOURCE_KINDS_ORDER,
  conditionOf,
  conditionForField,
  defaultCondition,
  fieldsFor,
  withOperator,
  type DraftAction,
  type EditorGroup,
  type Join,
  type RuleDraft,
} from './model.js';
import './rules.css';

export interface RuleEditorProps {
  value: RuleDraft;
  set(update: (draft: RuleDraft) => RuleDraft): void;
  /** The saved rule (null while creating): Run now needs it. */
  ruleId: RuleId | null;
  dirty: boolean;
  /** Shown at the top of the Rule section (e.g. "This row belongs to the topic …"). */
  note?: ReactNode;
  /** Extra content after the Then section (e.g. the filter's order controls). */
  children?: ReactNode;
}

const ACTION_OPTIONS = [
  ['move', 'Move to folder'],
  ['add-tag', 'Add tag'],
  ['remove-tag', 'Remove tag'],
  ['delete', 'Delete message'],
] as const;

export function RuleEditor({ value, set, ruleId, dirty, note, children }: RuleEditorProps) {
  const engine = useEngine();
  const folders = useFolders();
  const tags = useTags();
  const [test, setTest] = useState<RuleTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const isFilter = value.kind === 'filter';

  const setAction = (ai: number, next: DraftAction): void => set((d) => ({ ...d, actions: d.actions.map((a, i) => (i === ai ? next : a)) }));

  const runTest = async (): Promise<void> => {
    const form = document.getElementById('settings-form');
    if (form instanceof HTMLFormElement && !form.reportValidity()) return;
    setTesting(true);
    try {
      const result = await engine.rules.test({
        kind: value.kind,
        name: value.name || 'Test',
        icon: value.icon as never,
        enabled: value.enabled,
        triggerFolderId: isFilter ? (value.triggerFolderId as never) : null,
        condition: conditionOf(value),
        actions: [],
      });
      setTest(result);
      requestAnimationFrame(() => document.getElementById('rule-test-results')?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }));
    } catch (error) {
      reportError(error);
    } finally {
      setTesting(false);
    }
  };

  const runNow = async (): Promise<void> => {
    if (!ruleId || dirty) {
      showToast('Save the rule before running it');
      return;
    }
    try {
      const result = await engine.rules.runNow(ruleId);
      showToast(result.changed === 0 ? 'No messages changed' : `Rule applied · ${result.changed} change${result.changed === 1 ? '' : 's'}`, result.undoId);
    } catch (error) {
      reportError(error);
    }
  };

  const triggerName = folders.find((f) => f.id === value.triggerFolderId)?.name ?? 'Inbox';

  return (
    <>
      <SaveIslandTools>
        <Tool icon="play" label="Test rule" onClick={() => void runTest()} />
      </SaveIslandTools>
      <Section title="Rule">
        {note}
        <Field label="Name" name="name" value={value.name} required maxLength={80} onChange={(name) => set((d) => ({ ...d, name }))} />
        <IconField label="Icon" name="icon" value={value.icon} onChange={(icon) => set((d) => ({ ...d, icon }))} />
        {value.flattened ? <p className="field-help">This rule was built with deeper nesting elsewhere. Saving here keeps its groups but flattens anything inside them.</p> : null}
      </Section>
      {isFilter ? (
        <Section title="When to run">
          <Select
            label="Message enters"
            name="trigger"
            value={value.triggerFolderId ?? 'inbox'}
            options={folders.map((f) => [f.id, f.name] as const)}
            onChange={(triggerFolderId) => set((d) => ({ ...d, triggerFolderId }))}
          />
        </Section>
      ) : null}
      <Section title="Match conditions">
        <ConditionGroups
          use={value.kind}
          value={value}
          onChange={(next) => set((d) => ({ ...d, ...next }))}
          emptyNote="No conditions: this rule matches every scored message that reaches it (“otherwise”)."
        />
      </Section>
      <Section title="Then">
        <div className="rule-actions">
          {value.actions.map((a, ai) => (
            <div className="rule-action" key={ai}>
              <Select
                label="Action"
                name={`a${ai}-type`}
                value={a.type}
                options={ACTION_OPTIONS}
                onChange={(type) => setAction(ai, type === 'move' ? { type, folderId: 'priority' } : type === 'delete' ? { type } : { type, tag: 'tag' in a ? a.tag : '' })}
              />
              {a.type === 'move' ? (
                <Select label="Folder" name={`a${ai}-value`} value={a.folderId} options={folders.map((f) => [f.id, f.name] as const)} onChange={(folderId) => setAction(ai, { type: 'move', folderId })} />
              ) : a.type === 'delete' ? (
                <span className="action-note">Source statistics are preserved</span>
              ) : (
                <TagField name={`a${ai}-value`} value={a.tag} tags={tags.map((t) => t.name)} onChange={(tag) => setAction(ai, { type: a.type, tag })} />
              )}
              <Tool icon="x" label="Remove action" disabled={value.actions.length === 1} onClick={() => set((d) => ({ ...d, actions: d.actions.filter((_, i) => i !== ai) }))} />
            </div>
          ))}
        </div>
        <button type="button" className="add-line" onClick={() => set((d) => ({ ...d, actions: [...d.actions, { type: 'add-tag', tag: '' }] }))}>
          <Icon name="plus" /> Add action
        </button>
      </Section>
      {children}
      <div className="rule-test">
        <button className="secondary-button" type="button" disabled={testing} onClick={() => void runTest()}>
          <Icon name="play" /> Test rule
        </button>
        {isFilter ? (
          <button className="secondary-button" type="button" disabled={!ruleId} onClick={() => void runNow()} title={ruleId ? 'Apply this saved filter to the messages in its folder now' : 'Save the rule first'}>
            <Icon name="zap" /> Run now
          </button>
        ) : null}
        <span>Preview matches without moving messages</span>
      </div>
      <div id="rule-test-results" aria-live="polite">
        {test ? (
          <>
            <div className="test-summary">
              {test.matched} of {test.evaluated} messages match
              <span>{isFilter ? triggerName : 'Scored messages of the last 7 days'} · no actions applied</span>
            </div>
            {test.rows.map(({ row, result }) => (
              <div className="test-row" key={row.id} data-result={result}>
                <Icon name={result === 'match' ? 'check' : result === 'unknown' ? 'clock' : 'x'} />
                <span>{row.title || row.excerpt}</span>
                <b>{result === 'match' ? 'Match' : result === 'unknown' ? 'Pending' : 'No match'}</b>
              </div>
            ))}
          </>
        ) : null}
      </div>
    </>
  );
}

/** A tag by name: existing names are suggested; a new name creates the tag on save. */
function TagField({ name, value, tags, onChange }: { name: string; value: string; tags: string[]; onChange(value: string): void }) {
  const listId = `${name}-tags`;
  return (
    <label className="field">
      <span>Tag</span>
      <input type="text" name={name} value={value} required list={listId} maxLength={80} onChange={(e) => onChange(e.target.value)} />
      <datalist id={listId}>
        {tags.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </label>
  );
}

/** The condition tree of a rule or a model's run rule, as the editor shows it. */
export interface ConditionGroupsValue {
  join: Join;
  groups: EditorGroup[];
}

export interface ConditionGroupsProps {
  /** Which fields are offered (core `ALLOWED_FIELDS`): a filter, a scoring rule or a model's run rule. */
  use: QueryUse;
  value: ConditionGroupsValue;
  onChange(next: ConditionGroupsValue): void;
  /** Shown when there are no groups (a scoring rule's "otherwise", a run rule's "always"). */
  emptyNote: ReactNode;
  /** The engine's messages for one condition, keyed `<group>:<condition>` (shown under it). */
  issues?: ReadonlyMap<string, string>;
}

/**
 * Groups with ALL/ANY inside and one AND/OR across them (the prototype's `ruleEditor()` groups), shared by
 * Filters, scoring rules and a model's "Only if" (models.md §12). A filter keeps at least one group;
 * scoring rules and run rules may have none ("otherwise" / "always").
 */
export function ConditionGroups({ use, value, onChange, emptyNote, issues }: ConditionGroupsProps) {
  const folders = useFolders();
  const tags = useTags();
  const topics = useTopics();
  const sources = useSources();
  const { models } = useModels();
  const fields = fieldsFor(use);
  const keepOne = use === 'filter';

  const setGroups = (groups: EditorGroup[]): void => onChange({ join: value.join, groups });
  const setGroup = (gi: number, update: (g: EditorGroup) => EditorGroup): void => setGroups(value.groups.map((g, i) => (i === gi ? update(g) : g)));
  const setCondition = (gi: number, ci: number, next: ConditionNode): void =>
    setGroup(gi, (g) => ({ ...g, conditions: g.conditions.map((c, i) => (i === ci ? next : c)) }));

  const options = (field: ConditionField): PickerOption[] => {
    if (field === 'source') return sources.map((s) => ({ id: s.id, name: s.name, icon: sourceKindIcon(s.kind) }));
    if (field === 'source-type') return SOURCE_KINDS_ORDER.map((k) => ({ id: k, name: SOURCE_KIND_LABELS[k], icon: sourceKindIcon(k) }));
    if (field === 'tag') return tags.map((t) => ({ id: t.id, name: t.name, icon: t.icon }));
    if (field === 'topic') return topics.map((t) => ({ id: t.id, name: t.name, icon: t.icon }));
    if (field === 'script') return (Object.keys(SCRIPT_LABELS) as Array<keyof typeof SCRIPT_LABELS>).map((k) => ({ id: k, name: SCRIPT_LABELS[k], icon: 'languages' }));
    return folders.map((f) => ({ id: f.id, name: f.name, icon: f.icon }));
  };

  return (
    <>
      <div id="rule-groups">
        {value.groups.length === 0 ? <div className="editor-note rule-otherwise">{emptyNote}</div> : null}
        {value.groups.map((g, gi) => (
          <div key={gi}>
            {gi > 0 ? (
              <div className="group-join">
                {gi === 1 ? (
                  <select name="join" aria-label="Combine all groups" value={value.join === 'all' ? 'AND' : 'OR'} onChange={(e) => onChange({ join: (e.target.value === 'AND' ? 'all' : 'any') as Join, groups: value.groups })}>
                    <option>AND</option>
                    <option>OR</option>
                  </select>
                ) : (
                  <span>{value.join === 'all' ? 'AND' : 'OR'}</span>
                )}
              </div>
            ) : null}
            <div className="rule-group" data-group={gi}>
              <div className="rule-group-header">
                <span>Match</span>
                <select name={`g${gi}-match`} aria-label={`Group ${gi + 1} match mode`} value={g.operator === 'all' ? 'ALL' : 'ANY'} onChange={(e) => setGroup(gi, (x) => ({ ...x, operator: e.target.value === 'ALL' ? 'all' : 'any' }))}>
                  <option>ALL</option>
                  <option>ANY</option>
                </select>
                <span>of these conditions</span>
                {value.groups.length > 1 || !keepOne ? <Tool icon="x" label="Remove group" onClick={() => setGroups(value.groups.filter((_, i) => i !== gi))} /> : null}
              </div>
              {g.conditions.map((c, ci) => {
                const issue = issues?.get(`${gi}:${ci}`);
                return (
                  <div className={issue ? 'condition has-issue' : 'condition'} data-condition={ci} key={ci}>
                    <Select
                      label="Field"
                      name={`g${gi}c${ci}-field`}
                      value={c.field}
                      options={(fields.includes(c.field) ? fields : [c.field, ...fields]).map((f) => [f, FIELD_LABELS[f]] as const)}
                      onChange={(field) => setCondition(gi, ci, conditionForField(c, field, value.groups.flatMap((x) => x.conditions)))}
                    />
                    <Select
                      label="Operator"
                      name={`g${gi}c${ci}-operator`}
                      value={c.operator}
                      options={(FIELD_OPERATORS[c.field] ?? []).map((o) => [o, OPERATOR_LABELS[o] ?? o] as const)}
                      onChange={(op) => setCondition(gi, ci, withOperator(c, op))}
                    />
                    <ConditionValue condition={c} name={`g${gi}c${ci}-value`} options={options} topics={topics} onChange={(next) => setCondition(gi, ci, next)} />
                    <Tool
                      icon="x"
                      label="Remove condition"
                      disabled={g.conditions.length === 1}
                      onClick={() => setGroup(gi, (x) => ({ ...x, conditions: x.conditions.filter((_, i) => i !== ci) }))}
                    />
                    {c.field === 'best-score' || c.field === 'topic-score' ? (
                      <ScoreModelPicker name={`g${gi}c${ci}-model`} value={c.model ?? ''} models={models} onChange={(model) => {
                        const { model: _old, ...rest } = c;
                        setCondition(gi, ci, model ? ({ ...rest, model: model as ModelId } as ConditionNode) : (rest as ConditionNode));
                      }} />
                    ) : null}
                    {issue ? (
                      <p className="field-issue" role="alert" data-issue={`${gi}:${ci}`}>
                        {issue}
                      </p>
                    ) : null}
                  </div>
                );
              })}
              <button className="add-line" type="button" onClick={() => setGroup(gi, (x) => ({ ...x, conditions: [...x.conditions, defaultCondition(fields[0] ?? 'title')] }))}>
                <Icon name="plus" /> Add condition
              </button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="secondary-button" onClick={() => setGroups([...value.groups, { operator: 'all', conditions: [defaultCondition(fields[0] ?? 'title')] }])}>
        <Icon name="plus" /> Add group
      </button>
    </>
  );
}

/**
 * The model a score condition tests (models.md §8.1): a named model's own scores — the rule waits while
 * that model's score is pending — or the scores shown on the row (whichever model is first in order).
 */
function ScoreModelPicker({ name, value, models, onChange }: { name: string; value: string; models: readonly Model[]; onChange(model: string): void }) {
  const known = models.some((m) => m.id === value);
  return (
    <label className="field condition-model">
      <span>Scored by</span>
      <select name={name} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">The shown scores (first model in order)</option>
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
        {value && !known ? <option value={value}>{value} (unknown model)</option> : null}
      </select>
    </label>
  );
}

function ConditionValue({
  condition: c,
  name,
  options,
  topics,
  onChange,
}: {
  condition: ConditionNode;
  name: string;
  options(field: ConditionField): PickerOption[];
  topics: ReadonlyArray<{ id: string; name: string }>;
  onChange(next: ConditionNode): void;
}) {
  switch (c.field) {
    case 'title':
    case 'author':
    case 'body':
      return <Field label="Value" name={name} value={c.value} required onChange={(value) => onChange({ ...c, value })} />;
    case 'source':
    case 'source-type':
    case 'tag':
    case 'topic':
    case 'folder':
    case 'script': {
      const noun = { source: 'sources', 'source-type': 'types', tag: 'tags', topic: 'topics', folder: 'folders', script: 'writing systems' }[c.field];
      return (
        <div className="field rule-value">
          <span>Value</span>
          <MultiPicker noun={noun} options={options(c.field)} values={c.values} onChange={(values) => onChange({ ...c, values } as ConditionNode)} />
        </div>
      );
    }
    case 'read':
      return <Select label="Value" name={name} value={c.value} options={[['unread', 'Unread'], ['read', 'Read']]} onChange={(value) => onChange({ ...c, value })} />;
    case 'image':
      return <Select label="Value" name={name} value={c.value} options={[['yes', 'Has an image'], ['no', 'No image']]} onChange={(value) => onChange({ ...c, value })} />;
    case 'hours':
      return (
        <div className="field rule-value">
          <span>Value</span>
          <div className="rule-split">
            <input type="number" name={name} aria-label="Time in folder" min={0} step="any" required value={c.value} onChange={(e) => onChange({ ...c, value: Number(e.target.value) })} />
            <select aria-label="Unit" value={c.unit} onChange={(e) => onChange({ ...c, unit: e.target.value === 'days' ? 'days' : 'hours' })}>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </select>
          </div>
        </div>
      );
    case 'topic-score':
      return (
        <div className="field rule-value">
          <span>Value</span>
          <div className="rule-split">
            <select aria-label="Topic to score" value={c.topic ?? ''} onChange={(e) => {
              const { topic: _old, ...rest } = c;
              onChange(e.target.value ? { ...rest, topic: e.target.value as never } : rest);
            }}>
              <option value="">Any topic</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <input type="number" name={name} aria-label="Score out of 100" min={0} max={100} required value={c.value} onChange={(e) => onChange({ ...c, value: Number(e.target.value) as never })} />
          </div>
        </div>
      );
    case 'best-score':
      return <Field label="Value" name={name} type="number" min={0} max={100} required value={c.value} onChange={(value) => onChange({ ...c, value: Number(value) as never })} />;
  }
}
